export {
  FLASH_DATA_KEYS,
  type FlashDataKey,
  type BcrConnectFlashData,
  type InventoryDocumentCreateFlashData,
  type MessageClientsOptionsFlashData,
  type MessageClientsPromotionsTemplateFlashData,
  type SocialPostCreatorFlashData,
} from './keys'

import { type FlashDataKey } from './keys'

const STORAGE_PREFIX = 'booksy.frontdesk.flashData:'

const getSessionStorage = (): Storage | null => {
  if (typeof window === 'undefined' || !window.sessionStorage) return null
  return window.sessionStorage
}

const storageItemKey = (key: FlashDataKey): string => {
  return `${STORAGE_PREFIX}${key}`
}

export type ReadFlashDataOptions = {
  /**
   * When true (default), the value is removed from sessionStorage after a successful read
   * (or after a failed parse, so corrupt entries are not left behind).
   */
  consume?: boolean
}

type UseFlashDataResult = {
  save: <T>(key: FlashDataKey, data: T) => void
  read: <T>(key: FlashDataKey, options?: ReadFlashDataOptions) => T | null
  clear: (key: FlashDataKey) => void
}

export const useFlashData = (): UseFlashDataResult => {
  const clear = (key: FlashDataKey): void => {
    const storage = getSessionStorage()
    if (!storage) return

    storage.removeItem(storageItemKey(key))
  }

  const save = <T>(key: FlashDataKey, data: T): void => {
    const storage = getSessionStorage()
    if (!storage) return

    storage.setItem(storageItemKey(key), JSON.stringify(data))
  }

  const read = <T>(key: FlashDataKey, options?: ReadFlashDataOptions): T | null => {
    const consume = options?.consume !== false
    const storage = getSessionStorage()
    if (!storage) return null

    const rawValue = storage.getItem(storageItemKey(key))
    if (!rawValue) return null

    try {
      const parsed = JSON.parse(rawValue) as T
      if (consume) clear(key)
      return parsed
    } catch {
      if (consume) clear(key)
      return null
    }
  }

  return {
    save,
    read,
    clear,
  }
}
